<template>
  <article :class="$style.news">
    <header>
      <p :class="$style.news__datetime">
        <time :datetime="publishedAtISO">{{ publishedAt }}</time>
      </p>

      <h1 :class="$style.news__title">{{ articleDetail.title }}</h1>
    </header>

    <AppMarkdown
      :class="[$style.news__body, $style.body]"
      :body="articleDetail.body"
    />
  </article>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import { formatDate } from '@/libs/utilities/formatDate'
import AppMarkdown from '@/components/atoms/AppMarkdown.vue'

import type { AdminNewsDetailFragment } from './AdminNewsDetail.generated'

const props = defineProps<{ articleDetail: AdminNewsDetailFragment }>()
const publishedAt = computed(() =>
  formatDate(new Date(props.articleDetail.publishedAt))
)
const publishedAtISO = computed(() => {
  return new Date(props.articleDetail.publishedAt).toISOString()
})
</script>

<style lang="scss" module>
$border-gray: 1px solid $color-border-low-emphasis;
$cell-padding: 4px 16px;

.news {
  &__datetime {
    color: $color-text-and-object-high-emphasis;
    font-size: 16px;
    font-weight: bold;
  }

  &__title {
    color: $color-text-and-object-high-emphasis;
    font-size: 36px;
    font-weight: bold;
    line-height: 1.5;

    &:nth-child(n + 2) {
      margin-top: 48px;
    }
  }

  &__body {
    &:nth-child(n + 2) {
      margin-top: 48px;
    }
  }
}

.body {
  color: $color-text-and-object-high-emphasis;

  h1 {
    font-size: 28px;

    em {
      font-size: 0.75em;
    }

    &:nth-child(n + 2) {
      margin-top: 56px;
    }
  }

  h2 {
    font-size: 20px;

    em {
      font-size: 0.75em;
    }

    &:nth-child(n + 2) {
      margin-top: 48px;
    }
  }

  h3 {
    font-size: 20px;

    em {
      font-size: 0.75em;
    }

    &:nth-child(n + 2) {
      margin-top: 48px;
    }
  }

  p {
    line-height: 1.8;

    &:nth-child(n + 2) {
      margin-top: 32px;
    }
  }

  img {
    max-width: 100%;

    &:nth-child(n + 2) {
      margin-top: 32px;
    }
  }

  table {
    border-collapse: separate;
    border-spacing: 0;

    &:nth-child(n + 2) {
      margin-top: 32px;
    }

    th {
      padding: $cell-padding;
      background-color: $color-surface-accent-primary;
      color: $color-text-and-object-high-emphasis-inverse;
      font-size: 12px;
      font-weight: bold;

      &:first-child {
        border-top-left-radius: $border-radius-rounder;
      }

      &:last-child {
        border-top-right-radius: $border-radius-rounder;
      }
    }

    tr {
      &:nth-child(2n) {
        background-color: $color-surface-secondary;
      }

      td {
        padding: $cell-padding;
        border-left: $border-gray;
        font-size: 12px;

        &:last-child {
          border-right: $border-gray;
        }
      }

      &:last-child {
        td {
          border-bottom: $border-gray;

          &:first-child {
            border-bottom-left-radius: $border-radius-rounder;
          }

          &:last-child {
            border-bottom-right-radius: $border-radius-rounder;
          }
        }
      }
    }
  }

  ul {
    &:nth-child(n + 2) {
      margin-top: 32px;
    }

    li {
      list-style-position: inside;
      list-style-type: disc;

      &:nth-child(n + 2) {
        margin-top: 12px;
      }

      ul:first-child {
        padding-left: 20px;
        margin-top: 16px;

        li {
          list-style-type: circle;

          li {
            list-style-type: square;
          }
        }
      }
    }
  }
}
</style>
